import moment from "moment";
import { useCallback, useState, useEffect, useMemo } from "react";
import { pdf } from '@react-pdf/renderer';
import Swal from 'sweetalert2';
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import GraficoCircular from '../../../componentes/graficos/Circular';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisEvolucionVentas,
    AnalisisMetodosPagosVentas,
    AnalisisMoraVentas,
    AnalisisResumenKPIVentas
} from '../../../configuracion/apiUrls';
import Tabla from "../../../componentes/tablas/TablasImprimir";
import { formatoMoneda } from "../../../configuracion/validaciones";
import { mostraAlerta, mostraAlertaError } from "../../../componentes/alerts/sweetAlert";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";
import ReporteAnalisisVentas from "./ReporteAnalisisVentas";

moment.locale("es");

const VentasAnalisis = () => {
    const { usuario } = useContextUsuario();
    const [cargando, setCargando] = useState(true);
    const [metricas, setMetricas] = useState(null);
    const [chartPagos, setChartPagos] = useState(null);
    const [rawPagos, setRawPagos] = useState([]);
    const [chartEvolucion, setChartEvolucion] = useState(null);
    const [rawEvolucion, setRawEvolucion] = useState([]);
    const [datosMora, setDatosMora] = useState(null);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resKPI, resPagos, resEvol, resMora] = await Promise.all([
                AxiosPublico.get(AnalisisResumenKPIVentas, { params }),
                AxiosPublico.get(AnalisisMetodosPagosVentas, { params }),
                AxiosPublico.get(AnalisisEvolucionVentas, { params }),
                AxiosPublico.get(AnalisisMoraVentas, { params })
            ]);
            setMetricas(resKPI.data);
            setDatosMora(resMora.data);
            setRawPagos(resPagos.data || []);
            setRawEvolucion(resEvol.data || []);

            // 1. Gráfico Circular: Métodos de Pago
            if (resPagos.data?.length > 0) {
                setChartPagos({
                    labels: resPagos.data.map(p => p.metodo),
                    data: resPagos.data.map(p => {
                        const porcentaje = (parseFloat(p.total));
                        return parseFloat(porcentaje.toFixed(2));
                    }),
                    colors: ['#1cc88a', '#4e73df', '#36b9cc'],
                    titulo: "Distribución por Método de Pago"
                });
            } else {
                setChartPagos(null);
            }

            // 2. Gráfico Comparativo: Contado (Verde) vs Crédito (Azul)
            if (resEvol.data?.length > 0) {
                setChartEvolucion({
                    labels: resEvol.data.map(d => moment(d.fecha).format("DD/MM")),
                    datasets: [
                        {
                            label: "Contado",
                            data: resEvol.data.map(d => parseFloat(d.montoContado || 0)),
                            backgroundColor: '#1cc88a',
                        },
                        {
                            label: "Crédito",
                            data: resEvol.data.map(d => parseFloat(d.montoCredito || 0)),
                            backgroundColor: '#4e73df',
                        }
                    ],
                    titulo: "Evolución Diaria: Contado vs Crédito"
                });
            } else {
                setChartEvolucion(null);
            }

        } catch (error) {
            console.error("Error al cargar análisis de ventas:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const obtenerEtiquetaPeriodo = useCallback(() => {
        switch (filtro) {
            case "dia":
                return `Día ${moment(fechaSeleccionada).format('DD/MM/YYYY')}`;
            case "dia-hora":
                return `Día ${moment(fechaSeleccionada).format('DD/MM/YYYY')} a las ${horaSeleccionada}`;
            case "semana":
                return `Semana ${semanaSeleccionada}`;
            case "mes":
                return `Mes de ${moment(mesSeleccionado, 'YYYY-MM').format('MMMM [de] YYYY')}`;
            case "año":
                return `Año ${anioSeleccionado}`;
            default:
                return "Período Seleccionado";
        }
    }, [filtro, fechaSeleccionada, horaSeleccionada, semanaSeleccionada, mesSeleccionado, anioSeleccionado]);

    const handleImprimirReporte = async () => {
        if (!metricas) {
            mostraAlerta("No hay información de ventas cargada para generar el reporte.", "warning");
            return;
        }

        Swal.fire({
            title: 'Generando Reporte...',
            text: 'Preparando el documento PDF de análisis para impresión.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const fechas = obtenerFechasDeFiltro();
            const blob = await pdf(
                <ReporteAnalisisVentas
                    metricas={metricas}
                    chartPagos={chartPagos}
                    pagosRaw={rawPagos}
                    chartEvolucion={chartEvolucion}
                    evolucionRaw={rawEvolucion}
                    datosMora={datosMora}
                    periodoLabel={obtenerEtiquetaPeriodo()}
                    rangoFechas={fechas}
                    usuario={usuario}
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            Swal.close();
        } catch (error) {
            console.error("Error al generar PDF de análisis de ventas:", error);
            Swal.close();
            mostraAlertaError("Error al generar el reporte PDF. Inténtelo de nuevo.");
        }
    };

    const columnas = useMemo(
        () => [
            {
                title: "ID",
                data: "creditoId",
            },
            {
                title: "Cliente",
                data: null,
                render: (rowData) => {
                    const cliente = rowData.clienteNombre;
                    if (!cliente) return "Sin cliente";

                    const empresas = rowData.clienteEmpresa;

                    const clienteNombreHtml = cliente
                        ? `<div class="text-muted">${cliente}</div>`
                        : "";

                    return empresas || clienteNombreHtml
                        ? `${empresas}${clienteNombreHtml}`
                        : "Sin cliente";
                },
            },
            {
                title: 'Fecha',
                data: null,
                render: (rowData) => {
                    const fechaStr = rowData.fechaCredito;
                    if (fechaStr) {
                        const fecha = moment(fechaStr);
                        return fecha.isValid() ? fecha.format('DD/MM/YYYY') : '—';
                    }
                    return '—';
                },
            },
            {
                title: 'Vencimiento',
                data: null,
                render: (rowData) => {
                    const fechaStr = rowData.fechaVencimiento;
                    if (fechaStr) {
                        const fecha = moment(fechaStr);
                        return fecha.isValid() ? fecha.format('DD/MM/YYYY') : '—';
                    }
                    return '—';
                },
            },
            {
                title: "Saldo",
                data: "saldo",
                render: (data) => {
                    const valorFormateado = formatoMoneda(data);
                    return `<span style="text-align: right; display: block;">${valorFormateado}</span>`;
                }
            },
            {
                title: 'Dias Vencidos',
                data: null,
                render: (rowData) => {
                    const diasMora = rowData.diasMora;
                    let badgeClass = "bg-info text-white";

                    if (diasMora > 60) {
                        badgeClass = "bg-danger text-white";
                    } else if (diasMora >= 30) {
                        badgeClass = "bg-warning text-dark";
                    }
                    return `<span class="badge ${badgeClass}">${diasMora} Días</span>`;
                },
            }
        ],
        []
    );

    const datos = {
        titulo: {
            titulo: "Análisis de Ventas",
            url: "/app/admin/analisis",
            tituloUrl: "Analisis",
            nombreUrl: "Análisis de Ventas",
        },
    };

    return (
        <Page datos={datos}>
            <Card titulo="Filtros">
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
            </Card>

            {cargando ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2">Cargando indicadores...</p>
                </div>
            ) : (
                <>
                    <Card
                        titulo="Indicadores y Gráficos"
                        tools={
                            <button
                                type="button"
                                className="btn btn-primary btn-sm d-flex align-items-center gap-1 shadow-sm"
                                onClick={handleImprimirReporte}
                                title="Generar e imprimir reporte PDF"
                            >
                                <i className="fas fa-print me-1" />
                                <span>Imprimir Reporte</span>
                            </button>
                        }
                    >
                        {/* Barra superior de acciones y período */}
                        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom">
                            <div>
                                <span className="badge bg-primary text-uppercase px-2 py-1 me-2">
                                    {filtro.toUpperCase()}
                                </span>
                                <span className="text-muted fw-semibold">
                                    {obtenerEtiquetaPeriodo()}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 shadow-sm"
                                onClick={handleImprimirReporte}
                            >
                                <i className="fas fa-file-pdf text-danger" />
                                <span className="fw-semibold">Descargar / Imprimir PDF</span>
                            </button>
                        </div>

                        {/* 1. Tarjetas de KPIs */}
                        <div className="row">
                            <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
                                <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Total ventas</h6>
                                    <h3 className="fw-bold mb-0">{formatoMoneda(metricas?.totalNeto || 0)}</h3>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Contado</h6>
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <h3 className="fw-bold mb-0">{metricas?.porcentajeContado || 0}%</h3>
                                            <span className="text-secondary fw-medium small">{formatoMoneda(metricas?.montoContado || 0)}</span>
                                        </div>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className="progress-bar bg-success"
                                                style={{ width: `${metricas?.porcentajeContado || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-info border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Crédito</h6>
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <h3 className="fw-bold mb-0">{metricas?.porcentajeCredito || 0}%</h3>
                                            <span className="text-secondary fw-medium small">{formatoMoneda(metricas?.montoCredito || 0)}</span>
                                        </div>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className="progress-bar bg-info"
                                                style={{ width: `${metricas?.porcentajeCredito || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Tasa de mora</h6>
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <h3 className="fw-bold mb-0 text-danger">{metricas?.tasaMora || 0}%</h3>
                                            <span className="text-secondary fw-medium small">{formatoMoneda(datosMora?.totalVencido || 0)}</span>
                                        </div>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className="progress-bar bg-danger"
                                                style={{ width: `${metricas?.tasaMora || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 2. Gráficos */}
                        <div className="row">
                            {/* Gráfico Circular */}
                            <div className="col-lg-4 mb-4">
                                <div className="p-3 shadow-sm rounded border h-100">
                                    {chartPagos ? (
                                        <GraficoCircular datos={chartPagos} esMonetario={true} />
                                    ) : (
                                        <p className="text-center text-muted mt-5">Sin datos de pagos</p>
                                    )}
                                </div>
                            </div>

                            <div className="col-lg-8 mb-4">
                                <div className="p-3 shadow-sm rounded border h-100">
                                    {chartEvolucion ? (
                                        <GraficoBarra datos={chartEvolucion} esMonetario={true} />
                                    ) : (
                                        <p className="text-center text-muted mt-5">Sin datos de evolución</p>
                                    )}
                                </div>
                            </div>
                        </div>

                    </Card>

                    <Card titulo="Clientes con créditos vencidos">
                        <Tabla
                            datos={datosMora?.detalle || []}
                            columnas={columnas}
                            id="tablaClientesVencidos"
                        />
                    </Card>
                </>
            )}
        </Page>
    );
};

export default VentasAnalisis;
